/**
 * MembersSection Component
 *
 * Visar en lista av företagsmedlemmar och väntande inbjudningar.
 * Owner/manager kan ändra roller, ta bort medlemmar och bjuda in nya.
 * Viewer/editor ser listan utan åtgärdsknappar.
 */

import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Trash2,
  Mail,
  Clock,
  ChevronDown,
  Loader2,
} from "lucide-react";
import {
  membersApi,
  invitationsApi,
  type CompanyMember,
  type CompanyInvitation,
} from "@/lib/api";
import { InviteMemberModal } from "./InviteMemberModal";

// ─── Rollkonfiguration ──────────────────────────────────────────────

type MemberRole = "owner" | "manager" | "editor" | "viewer";

const ROLE_LABELS: Record<MemberRole, string> = {
  owner: "Ägare",
  manager: "Administratör",
  editor: "Redigerare",
  viewer: "Läsbehörighet",
};

const ROLE_COLORS: Record<MemberRole, string> = {
  owner: "bg-purple-100 text-purple-700",
  manager: "bg-blue-100 text-blue-700",
  editor: "bg-green-100 text-green-700",
  viewer: "bg-gray-100 text-gray-700",
};

const ASSIGNABLE_ROLES: MemberRole[] = ["manager", "editor", "viewer"];

// ─── Hjälpfunktioner ────────────────────────────────────────────────

function getInitials(
  firstName: string | null,
  lastName: string | null,
  email: string
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

function getDisplayName(member: CompanyMember): string {
  if (member.firstName && member.lastName) {
    return `${member.firstName} ${member.lastName}`;
  }
  if (member.firstName) {
    return member.firstName;
  }
  return member.email;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function isExpired(dateString: string): boolean {
  return new Date(dateString) < new Date();
}

// ─── Props ──────────────────────────────────────────────────────────

interface MembersSectionProps {
  companyId: string;
  userRole: string;
  onCountChange?: (count: number) => void;
}

// ─── Komponent ──────────────────────────────────────────────────────

export function MembersSection({ companyId, userRole, onCountChange }: MembersSectionProps) {
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [invitations, setInvitations] = useState<CompanyInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Spåra pågående operationer per medlem/inbjudan
  const [changingRoleId, setChangingRoleId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const canManage = userRole === "owner" || userRole === "manager";

  // ── Ladda data ──────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    try {
      const [membersRes, invitationsRes] = await Promise.all([
        membersApi.list(companyId),
        invitationsApi.list(companyId),
      ]);

      if (membersRes.success && membersRes.data) {
        setMembers(membersRes.data.members);
        onCountChange?.(membersRes.data.members.length);
      }
      if (invitationsRes.success && invitationsRes.data) {
        setInvitations(invitationsRes.data.invitations);
      }
    } catch {
      toast.error("Kunde inte ladda medlemmar");
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Hantera rollbyte ────────────────────────────────────────────

  const handleRoleChange = async (memberId: string, newRole: string) => {
    setChangingRoleId(memberId);
    try {
      const response = await membersApi.changeRole(companyId, memberId, newRole);
      if (response.success) {
        setMembers((prev) =>
          prev.map((m) =>
            m.id === memberId ? { ...m, role: newRole as MemberRole } : m
          )
        );
        toast.success("Rollen har uppdaterats");
      } else {
        toast.error(response.error?.message || "Kunde inte ändra roll");
      }
    } catch {
      toast.error("Ett fel uppstod vid ändring av roll");
    } finally {
      setChangingRoleId(null);
    }
  };

  // ── Hantera borttagning av medlem ───────────────────────────────

  const handleRemoveMember = async (memberId: string) => {
    setRemovingId(memberId);
    try {
      const response = await membersApi.remove(companyId, memberId);
      if (response.success) {
        setMembers((prev) => prev.filter((m) => m.id !== memberId));
        toast.success("Medlemmen har tagits bort");
      } else {
        toast.error(response.error?.message || "Kunde inte ta bort medlemmen");
      }
    } catch {
      toast.error("Ett fel uppstod vid borttagning");
    } finally {
      setRemovingId(null);
      setConfirmRemoveId(null);
    }
  };

  // ── Hantera avbrytande av inbjudan ──────────────────────────────

  const handleCancelInvitation = async (invitationId: string) => {
    setCancellingId(invitationId);
    try {
      const response = await invitationsApi.cancel(companyId, invitationId);
      if (response.success) {
        setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
        toast.success("Inbjudan har dragits tillbaka");
      } else {
        toast.error(
          response.error?.message || "Kunde inte avbryta inbjudan"
        );
      }
    } catch {
      toast.error("Ett fel uppstod vid avbrytning av inbjudan");
    } finally {
      setCancellingId(null);
    }
  };

  // ── Laddningsvy ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500">
            Laddar medlemmar...
          </span>
        </div>
      </div>
    );
  }

  // ── Avgör om rollväljaren kan ändra en specifik medlem ──────────

  function canChangeRole(member: CompanyMember): boolean {
    if (!canManage) return false;
    // Ägare kan inte ändras av managers
    if (member.role === "owner" && userRole === "manager") return false;
    // Ägare kan inte ändra sin egen roll (det finns alltid minst en ägare)
    if (member.role === "owner" && userRole === "owner") return false;
    return true;
  }

  function canRemoveMember(member: CompanyMember): boolean {
    if (!canManage) return false;
    // Man kan inte ta bort ägare om man är manager
    if (member.role === "owner" && userRole === "manager") return false;
    // Ägare kan inte ta bort sig själv
    if (member.role === "owner" && userRole === "owner") return false;
    return true;
  }

  // ── Rendera rollbadge eller dropdown ────────────────────────────

  function renderRoleBadge(member: CompanyMember) {
    const role = member.role as MemberRole;

    if (canChangeRole(member)) {
      return (
        <div className="relative">
          {changingRoleId === member.id ? (
            <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500">
              <Loader2 size={12} className="animate-spin mr-1" />
              Sparar...
            </span>
          ) : (
            <div className="relative inline-flex items-center">
              <select
                value={role}
                onChange={(e) => handleRoleChange(member.id, e.target.value)}
                className={`appearance-none text-xs px-2 py-1 pr-6 rounded-full cursor-pointer border-0 focus:ring-2 focus:ring-primary-300 ${ROLE_COLORS[role]}`}
              >
                {/* Visa nuvarande roll även om den inte är bland "tilldelingsbara" (t.ex. owner) */}
                {role === "owner" && (
                  <option value="owner">{ROLE_LABELS.owner}</option>
                )}
                {ASSIGNABLE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={12}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60"
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <span
        className={`text-xs px-2 py-1 rounded-full ${ROLE_COLORS[role]}`}
      >
        {ROLE_LABELS[role]}
      </span>
    );
  }

  // ── Huvudvy ─────────────────────────────────────────────────────

  const pendingInvitations = invitations.filter(
    (i) => !isExpired(i.expiresAt)
  );
  const hasInvitations = pendingInvitations.length > 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
      {/* Rubrik */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-600 rounded-xl flex items-center justify-center shadow-lg shadow-slate-500/25 flex-shrink-0">
            <Users size={20} className="text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900">Medlemmar</h2>
            <p className="text-xs text-gray-500">Hantera behörigheter och bjud in användare</p>
          </div>
        </div>
        {canManage && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 px-3 sm:px-4 py-2 sm:py-2.5 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto flex-shrink-0"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center justify-center sm:justify-start gap-2 text-white font-medium">
              <UserPlus size={16} />
              Bjud in
            </div>
          </button>
        )}
      </div>

      {/* Medlemslista */}
      {members.length === 0 ? (
        <div className="text-center py-8">
          <Users className="mx-auto text-gray-300 mb-3" size={40} />
          <p className="text-gray-600">Inga medlemmar hittades</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-3 border border-gray-100 rounded-lg"
            >
              <div className="flex items-center space-x-3 min-w-0">
                {/* Avatar med initialer */}
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-medium text-sm flex-shrink-0">
                  {getInitials(member.firstName, member.lastName, member.email)}
                </div>

                {/* Namn och e-post */}
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">
                    {getDisplayName(member)}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {member.email}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3 flex-shrink-0 ml-3">
                {/* Rollbadge / dropdown */}
                {renderRoleBadge(member)}

                {/* Medlemsdatum */}
                <span className="text-xs text-gray-400 hidden sm:inline">
                  {formatDate(member.createdAt)}
                </span>

                {/* Ta bort-knapp */}
                {canRemoveMember(member) && (
                  <div className="relative">
                    {confirmRemoveId === member.id ? (
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-600 whitespace-nowrap">
                          Är du säker?
                        </span>
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          disabled={removingId === member.id}
                          className="text-xs px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          {removingId === member.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            "Ja"
                          )}
                        </button>
                        <button
                          onClick={() => setConfirmRemoveId(null)}
                          className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                        >
                          Nej
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRemoveId(member.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Ta bort medlem"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Väntande inbjudningar */}
      {hasInvitations && (
        <>
          <div className="border-t border-gray-200 my-6" />
          <div className="mb-4 flex items-center space-x-2">
            <Mail size={16} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">
              Väntande inbjudningar
            </h3>
            <span className="text-xs text-gray-400">
              ({pendingInvitations.length})
            </span>
          </div>
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between p-3 border border-dashed border-gray-200 rounded-lg bg-gray-50/50"
              >
                <div className="flex items-center space-x-3 min-w-0">
                  {/* Ikon istället för avatar */}
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 flex-shrink-0">
                    <Mail size={16} />
                  </div>

                  {/* E-post */}
                  <div className="min-w-0">
                    <p className="font-medium text-gray-700 text-sm truncate">
                      {invitation.email}
                    </p>
                    <div className="flex items-center space-x-1 text-xs text-gray-400">
                      <Clock size={11} />
                      <span>
                        Förfaller {formatDate(invitation.expiresAt)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 flex-shrink-0 ml-3">
                  {/* Rollbadge */}
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      ROLE_COLORS[invitation.role as MemberRole] ||
                      ROLE_COLORS.viewer
                    }`}
                  >
                    {ROLE_LABELS[invitation.role as MemberRole] ||
                      invitation.role}
                  </span>

                  {/* Avbryt-knapp */}
                  {canManage && (
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      disabled={cancellingId === invitation.id}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      title="Dra tillbaka inbjudan"
                    >
                      {cancellingId === invitation.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Inbjudningsmodal */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        companyId={companyId}
        onInvited={loadData}
      />
    </div>
  );
}
